import { Button } from '@crowdex/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronDown, Copy, Folder, GitBranch, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { branchSwitchStep, useBranchActions } from '@/lib/desktop-shell/branch-actions';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { shortcutLabel } from '@/lib/platform';

export type BranchActionsState = {
  branch: string;
  /** The branch it started from ("main"). */
  base: string | null;
  /** Its commits the base does not have. */
  ahead: number;
  /** The base's commits it does not have yet, when known. */
  behind: number | null;
  /** Other branches it can switch to. */
  branches: readonly string[];
};

const ROW =
  'flex h-9 w-full items-center gap-3 rounded-lg px-2.5 text-left text-foreground type-ui transition-colors hover:bg-control disabled:opacity-50 disabled:hover:bg-transparent';

/**
 * The branch's actions, anchored to its chip in the header (design D45, a sheet on a phone:
 * mobile 42): where it stands against its base on top ("From main · 3 commits ahead", and
 * "main moved on" with Update from main when the base has new commits), then Switch branch…,
 * Create a branch from here, Continue in a new worktree, Copy branch name.
 */
export function BranchActionsMenu({
  state,
  onUpdateFromBase,
  onSwitchBranch,
  onCreateBranch,
  onContinueInWorktree,
  defaultOpen = false,
  compact = false,
  align = 'start',
  shortcut = true,
}: {
  state: BranchActionsState;
  onUpdateFromBase?: () => void;
  onSwitchBranch?: (branch: string) => void;
  onCreateBranch?: () => void;
  onContinueInWorktree?: () => void;
  defaultOpen?: boolean;
  /** In a phone's header: as small as its subtitle. */
  compact?: boolean;
  /** Where its popover sits against the chip (`end` at the right of the Summary). */
  align?: 'start' | 'end';
  /** It answers ⌘B (one chip does: the header's). */
  shortcut?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [switching, setSwitching] = useState(false);
  const [query, setQuery] = useState('');
  // A phone's sheet (42) has no keyboard to name.
  const touchScreen = useMediaQuery('(hover: none) and (pointer: coarse)');
  useEffect(() => {
    if (!open) {
      setSwitching(false);
      setQuery('');
    }
  }, [open]);
  // ⌘B opens Switch branch… from anywhere in the conversation.
  useEffect(() => {
    if (onSwitchBranch === undefined || !shortcut) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'b' && (event.metaKey || event.ctrlKey) && !event.shiftKey) {
        event.preventDefault();
        setOpen(true);
        setSwitching(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSwitchBranch, shortcut]);
  const run = (action: (() => void) | undefined) => () => {
    setOpen(false);
    action?.();
  };
  const others = state.branches
    .filter((branch) => branch !== state.branch)
    .filter((branch) => branch.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label={`Branch ${state.branch}`}
          className={cn(
            'inline-flex min-w-0 items-center gap-1.5 rounded-control font-mono text-muted-foreground transition-colors hover:bg-control hover:text-foreground',
            compact ? 'h-4 gap-1 px-1 text-caption' : 'h-7 px-2 text-xs',
          )}
          data-branch-chip
          type="button"
        >
          <GitBranch aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">{state.branch}</span>
          <ChevronDown aria-hidden className="size-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent aria-label="Branch" align={align} className="w-90 p-1" data-branch-actions>
        <div className="px-2.5 pt-2 pb-2">
          <p className="type-ui truncate font-mono font-semibold text-foreground">{state.branch}</p>
          {state.base !== null && (
            <p className="type-meta text-muted-foreground">
              From {state.base}
              {state.ahead > 0
                ? ` · ${state.ahead} ${state.ahead === 1 ? 'commit' : 'commits'} ahead`
                : ''}
            </p>
          )}
        </div>
        {state.base !== null && state.behind !== null && state.behind > 0 && (
          <div className="mx-1 mb-1 flex items-center gap-3 rounded-xl bg-control px-3 py-2">
            <p className="type-meta flex-1 text-foreground">
              {state.base} moved on: {state.behind} new {state.behind === 1 ? 'commit' : 'commits'}.
              {onUpdateFromBase === undefined ? '' : ' Bring them in?'}
            </p>
            {onUpdateFromBase !== undefined && (
              <Button
                className="h-8 shrink-0 rounded-control px-3.5"
                onClick={run(onUpdateFromBase)}
                size="sm"
              >
                Update from {state.base}
              </Button>
            )}
          </div>
        )}
        {switching ? (
          <div className="grid gap-1 p-1">
            <label className="flex h-9 items-center gap-2 rounded-lg bg-control px-2.5">
              <Search aria-hidden className="size-3.5 text-muted-foreground" />
              <input
                aria-label="Find a branch"
                // biome-ignore lint/a11y/noAutofocus: the list opened to pick a branch.
                autoFocus
                className="type-ui min-w-0 flex-1 bg-transparent outline-none"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a branch…"
                value={query}
              />
            </label>
            <div className="max-h-64 overflow-y-auto">
              {others.map((branch) => (
                <button
                  className={cn(ROW, 'font-mono text-xs')}
                  key={branch}
                  onClick={run(() => onSwitchBranch?.(branch))}
                  type="button"
                >
                  <GitBranch aria-hidden className="size-3.5 text-muted-foreground" />
                  <span className="truncate">{branch}</span>
                </button>
              ))}
              {others.length === 0 && (
                <p className="type-meta px-2.5 py-2 text-muted-foreground">No other branch</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid">
            {onSwitchBranch !== undefined && (
              <button className={ROW} onClick={() => setSwitching(true)} type="button">
                <GitBranch aria-hidden className="size-4 text-muted-foreground" />
                <span className="flex-1">Switch branch…</span>
                {!touchScreen && (
                  <kbd className="rounded-md border border-hairline px-1 font-sans text-caption text-muted-foreground">
                    {shortcutLabel({ key: 'B', commandOrControl: true })}
                  </kbd>
                )}
              </button>
            )}
            {onCreateBranch !== undefined && (
              <button className={ROW} onClick={run(onCreateBranch)} type="button">
                <Plus aria-hidden className="size-4 text-muted-foreground" />
                Create a branch from here
              </button>
            )}
            {onContinueInWorktree !== undefined && (
              <button className={ROW} onClick={run(onContinueInWorktree)} type="button">
                <Folder aria-hidden className="size-4 text-muted-foreground" />
                Continue in a new worktree
              </button>
            )}
            <button
              className={ROW}
              onClick={run(() => void navigator.clipboard?.writeText(state.branch))}
              type="button"
            >
              <Copy aria-hidden className="size-4 text-muted-foreground" />
              Copy branch name
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The header's branch chip with its actions, from the conversation's git status: switching
 * and creating go through the shell's branch dialogs.
 */
export function ConversationBranchChip({
  onUpdateFromBase,
  onContinueInWorktree,
  behind = null,
  compact = false,
  defaultOpen = false,
  align,
  shortcut,
}: {
  onUpdateFromBase?: () => void;
  onContinueInWorktree?: () => void;
  behind?: number | null;
  compact?: boolean;
  defaultOpen?: boolean;
  align?: 'start' | 'end';
  shortcut?: boolean;
}) {
  const store = useOptionalDesktopShellStoreApi();
  const actions = useBranchActions();
  const previewBehind = useDesktopPreviewState((state) => state.branchBehind);
  const [gitStatus, setGitStatus] = useState(() => store?.getState().gitStatus ?? null);
  useEffect(() => {
    if (store === null) return;
    setGitStatus(store.getState().gitStatus);
    return store.subscribe((state) => setGitStatus(state.gitStatus));
  }, [store]);
  if (store === null || gitStatus === null || gitStatus.branch === '') return null;
  const switchTo = (branch: string) => {
    const target = { branch, remote: !gitStatus.branches.includes(branch) };
    const state = store.getState();
    const step = branchSwitchStep(state, target);
    const holder = state.gitStatus?.worktrees.find(
      (tree) => !tree.isCurrent && tree.branch === branch,
    );
    if (step === 'open-worktree' && holder !== undefined)
      state.setBranchInWorktree({ branch, path: holder.path });
    else if (step === 'confirm') state.setPendingBranchSwitch(target);
    else if (step === 'switch') void actions.switchBranch?.(target);
  };
  return (
    <BranchActionsMenu
      compact={compact}
      {...(align === undefined ? {} : { align })}
      {...(shortcut === undefined ? {} : { shortcut })}
      defaultOpen={defaultOpen}
      {...(actions.createBranch === null ? {} : { onCreateBranch: actions.createBranch })}
      {...(actions.switchBranch === null ? {} : { onSwitchBranch: switchTo })}
      state={{
        branch: gitStatus.branch,
        base: gitStatus.defaultBranch,
        ahead: gitStatus.commitsAhead,
        behind: behind ?? previewBehind,
        branches: [...new Set([...gitStatus.branches, ...gitStatus.remoteBranches])],
      }}
      {...(onUpdateFromBase === undefined ? {} : { onUpdateFromBase })}
      {...(onContinueInWorktree === undefined ? {} : { onContinueInWorktree })}
    />
  );
}
